import { v4 as uuidv4 } from 'uuid';
import { formatFileSize, normalizeLocalFileItem } from './localFile.js';
import { pickFilesizeByte } from './mediaItem.js';
import { LOCAL_ASSET } from './localAssets.js';
import { getMediaItemUrl } from './qiniuMedia.js';

export const PAY_FILE_COLLECTION_TYPE = 'pay-file-collection';
export const PAY_FILE_COLLECTION_DEFAULT_BORDER_RADIUS = 12;
export const PAY_FILE_COLLECTION_DEFAULT_ITEM_COUNT = 4;
export const PAY_FILE_COLLECTION_COMPACT_FREE_COUNT = 2;
export const PAY_FILE_COLLECTION_COMPACT_MAX_ITEMS = 3;

export const DEFAULT_PAY_FILE_COLLECTION_FOOTER_FEATURES = [
	{ id: uuidv4(), text: '安全加密保障', icon: 'shield' },
	{ id: uuidv4(), text: '支持下载', icon: 'download' },
	{ id: uuidv4(), text: '永久有效', icon: 'valid' },
];

const DEMO_FILES = LOCAL_ASSET.payFileCollection || [];

function getFixedFooterFeatures() {
	return DEFAULT_PAY_FILE_COLLECTION_FOOTER_FEATURES.map(item => ({ ...item, id: uuidv4() }));
}

function getFileTypeIconKey(fileName = '') {
	const ext = (fileName.split('.').pop() || '').toLowerCase();
	if (ext === 'pdf') return 'pdf';
	if (['doc', 'docx'].includes(ext)) return 'word';
	if (['xls', 'xlsx'].includes(ext)) return 'excel';
	if (['ppt', 'pptx'].includes(ext)) return 'powerpoint';
	if (ext === 'txt') return 'txt';
	if (['zip', 'rar', '7z'].includes(ext)) return 'zip';
	return 'file';
}

export function getPayFileCollectionFileIconUrl(fileName = '') {
	const key = getFileTypeIconKey(fileName);
	return LOCAL_ASSET.payFileIcons[key] || LOCAL_ASSET.payFileIcons.file;
}

export function getPayFileCollectionFileType(fileName = '') {
	const ext = (fileName.split('.').pop() || '').toLowerCase();
	if (ext === 'pdf') {
		return { label: 'PDF 文档', mark: 'PDF', tone: 'pdf' };
	}
	if (['doc', 'docx'].includes(ext)) {
		return { label: 'Word 文档', mark: 'W', tone: 'word' };
	}
	if (['xls', 'xlsx'].includes(ext)) {
		return { label: 'Excel 表格', mark: 'X', tone: 'excel' };
	}
	if (['ppt', 'pptx'].includes(ext)) {
		return { label: 'PPT 文档', mark: 'PPT', tone: 'ppt' };
	}
	return { label: '文档', mark: 'FILE', tone: 'file' };
}

function createDemoItem(preset = {}, index = 0) {
	const demo = DEMO_FILES[index % DEMO_FILES.length] || {};
	const filename = preset.filename || demo.filename || '';
	const fileType = getPayFileCollectionFileType(filename);
	const fileUrl = preset.url || preset.previewUrl || demo.url || '';
	return normalizePayFileCollectionItem({
		id: preset.id || uuidv4(),
		filename,
		title: preset.title || (filename ? filename.replace(/\.[^.]+$/, '') : '未命名文档'),
		filesize: preset.filesize || demo.filesize,
		filesize_byte: pickFilesizeByte(preset) || pickFilesizeByte(demo),
		url: fileUrl,
		previewUrl: preset.previewUrl || demo.previewUrl || fileUrl,
		local: false,
		tone: preset.tone || fileType.tone,
	});
}

function normalizePayFileCollectionItem(item = {}, index = 0) {
	const normalized = normalizeLocalFileItem(item);
	const fallback = DEMO_FILES.length
		? DEMO_FILES[index % DEMO_FILES.length]
		: null;
	const filename = normalized.filename || fallback?.filename || '';
	const fileType = getPayFileCollectionFileType(filename);
	return {
		...normalized,
		filename,
		title: String(item.title || filename.replace(/\.[^.]+$/, '') || '未命名文档').trim(),
		filesize: normalized.filesize || formatFileSize(pickFilesizeByte(normalized) || 0),
		tone: item.tone || fileType.tone,
	};
}

function normalizePayFileCollectionItems(items = []) {
	if (!items || items.length === 0) {
		return [];
	}
	return items.map((item, index) => normalizePayFileCollectionItem(item, index));
}

function getTotalFileSize(items = []) {
	const bytes = items.reduce((sum, item) => sum + (pickFilesizeByte(item) || 0), 0);
	return formatFileSize(bytes);
}

export function normalizePayFileCollectionData(data = {}) {
	const items = normalizePayFileCollectionItems(data.items);
	const freePreviewCount = Number(data.freePreviewCount);
	const initExpandCount = Number(data.initExpandCount);
	return {
		title: String(data.title || '商业计划书资料合集').trim(),
		price: data.price ?? '9.90',
		currency: data.currency || '¥',
		buttonText: data.buttonText || '解锁全部文档',
		buttonBg: data.buttonBg || data.buttonBgStart || '#285af9',
		freePreviewCount: Number.isFinite(freePreviewCount) && freePreviewCount >= 0
			? Math.min(freePreviewCount, items.length)
			: 2,
		initExpandCount: Number.isFinite(initExpandCount) && initExpandCount >= 0
			? Math.min(initExpandCount, items.length)
			: 3,
		footerFeatures: getFixedFooterFeatures(),
		items,
		borderRadius: Math.max(0, Number(data.borderRadius) || PAY_FILE_COLLECTION_DEFAULT_BORDER_RADIUS),
	};
}

export function getPayFileCollectionDefaults(extra = {}) {
	return normalizePayFileCollectionData({
		items: [],
		freePreviewCount: 2,
		...extra,
	});
}

export function getPayFileCollectionPreviewDefaults() {
	const count = DEMO_FILES.length || PAY_FILE_COLLECTION_DEFAULT_ITEM_COUNT;
	return normalizePayFileCollectionData({
		items: Array.from({ length: count }, (_, index) => createDemoItem({}, index)),
		freePreviewCount: 2,
	});
}

export function isPayFileCollectionBlock(type) {
	return type === PAY_FILE_COLLECTION_TYPE;
}

export function getPayFileCollectionMetaText(data = {}) {
	const normalized = normalizePayFileCollectionData(data);
	const count = normalized.items.length;
	const totalSize = getTotalFileSize(normalized.items);
	return `共 ${count} 份文档 · ${totalSize}`;
}

export function getPayFileCollectionUnlockHint(data = {}) {
	const normalized = normalizePayFileCollectionData(data);
	return `解锁全部 ${normalized.items.length} 份文档`;
}

export function getPayFileCollectionListEntries(data = {}, { unlocked = false, compact = false, unfolded = false } = {}) {
	const normalized = normalizePayFileCollectionData(data);
	const pageCount = normalized.items.length;
	const freeCount = compact
		? Math.min(PAY_FILE_COLLECTION_COMPACT_FREE_COUNT, normalized.freePreviewCount, pageCount)
		: normalized.freePreviewCount;

	if (unlocked || unfolded) {
		const items = compact
			? normalized.items.slice(0, PAY_FILE_COLLECTION_COMPACT_MAX_ITEMS)
			: normalized.items;
		return items.map((item, index) => ({
			key: item.id,
			item,
			index,
			locked: !unlocked && index >= freeCount,
		}));
	}

	const entries = [];
	normalized.items.slice(0, freeCount).forEach((item, index) => {
		entries.push({
			key: item.id,
			item,
			index,
			locked: false,
		});
	});

	if (pageCount > freeCount) {
		const lockIndex = freeCount;
		entries.push({
			key: `locked-${normalized.items[lockIndex]?.id || lockIndex}`,
			item: normalized.items[lockIndex] || normalized.items[freeCount - 1] || { id: 'locked-summary', title: '更多文档' },
			index: lockIndex,
			locked: true,
		});
	}

	if (compact) {
		return entries.slice(0, PAY_FILE_COLLECTION_COMPACT_MAX_ITEMS);
	}

	return entries;
}

export function getPayFileCollectionCardStyle(data = {}, { h5 = false } = {}) {
	const normalized = normalizePayFileCollectionData(data);
	const pxRatio = h5 ? 50 : 1;
	const radius = normalized.borderRadius;
	return {
		borderRadius: h5 ? `${radius / pxRatio}rem` : `${radius}px`,
	};
}

export function formatPayFileCollectionIndex(index = 0) {
	return String(index + 1).padStart(2, '0');
}

export function getPayFileCollectionItemMeta(item = {}) {
	const fileType = getPayFileCollectionFileType(item.filename);
	return `${fileType.label} · ${item.filesize || formatFileSize(pickFilesizeByte(item) || 0)}`;
}

export function getPayFileCollectionUnlockKey(blockId) {
	return blockId ? `gedit-pay-file-collection-${blockId}` : '';
}

export function isPayFileCollectionUnlocked(blockId) {
	const key = getPayFileCollectionUnlockKey(blockId);
	if (!key || typeof sessionStorage === 'undefined') return false;
	return sessionStorage.getItem(key) === '1';
}

export function setPayFileCollectionUnlocked(blockId, unlocked = true) {
	const key = getPayFileCollectionUnlockKey(blockId);
	if (!key || typeof sessionStorage === 'undefined') return;
	if (unlocked) {
		sessionStorage.setItem(key, '1');
	} else {
		sessionStorage.removeItem(key);
	}
}

export function getPayFileCollectionFileUrl(item = {}) {
	return getMediaItemUrl(item);
}
